import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Column } from 'primereact/column';
import {
	DataTable,
	DataTablePFSEvent,
	DataTableSortOrderType,
	DataTableSelectAllChangeParams,
	DataTableSelectionChangeParams
} from 'primereact/datatable';
import { PaginatorPageState } from 'primereact/paginator';

import './PeriodStatus.scss';

import Filters from './filters/Filters';
import Pagination from '../common/pagination/Pagination';
import { DropdownOption } from '../../models/common/DropdownOption';
import { UserResponseModel } from '../../models/users/UserResponseModel';
import { PeriodInputStatusesSearchResponseModel } from '../../models/periodInputStatuses/PeriodInputStatusesSearchResponseModel';
import { PeriodInputStatusesListingResponseModel } from '../../models/periodInputStatuses/PeriodInputStatusesListingResponseModel';
import { HttpService, IHttpService } from '../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../services/notifierService';
import {
	getCurrentQuarter,
	getCurrentYear,
	nameof
} from '../../shared/utility';
import { IState } from '../../store/IState';
import { QuarterlyPeriodsResponseModel } from '../../models/quarterlyPeriods/QuarterlyPeriodsResponseModel';
import { PeriodInputStatusUpdateRequestModel } from '../../models/periodInputStatuses/PeriodInputStatusUpdateRequestModel';
import { Routes } from '../../routes';
import { useNavigate } from 'react-router-dom';
import { PeriodInputStatusesSearchRequestModel } from '../../models/periodInputStatuses/PeriodInputStatusesSearchRequestModel';
import { useCookies } from 'react-cookie';

const PeriodStatus = (): JSX.Element => {
	const [first, setFirst] = useState<number>(0);
	const [rows, setRows] = useState<number>(12);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();
	const [loading, setLoading] = useState<boolean>(false);
	const [shouldReload, setShouldReload] = useState<boolean>(false);
	const [cookies, setCookie] = useCookies([
		'periodStatusDivisions',
		'periodStatusCountries',
		'periodStatusQuarters',
		'periodStatusYears',
		'periodStatusStatuses'
	]);

	const navigate = useNavigate();

	const [divisionFilter, setDivisionFilter] = useState<string | number[]>([]);
	const [countryFilter, setCountryFilter] = useState<string | number[]>([]);
	const [periodQuarterFilter, setPeriodQuarterFilter] = useState<string[]>([
		getCurrentQuarter()
	]);
	const [periodYearFilter, setPeriodYearFilter] = useState<number[]>([
		getCurrentYear()
	]);
	const [statusFilter, setStatusFilter] = useState<string | number[]>([]);

	const [periodStatusesSearch, setPeriodStatusesSearch] =
		useState<PeriodInputStatusesSearchResponseModel>();
	const [periodStatusesListing, setPeriodStatusesListing] = useState<
		PeriodInputStatusesListingResponseModel[]
	>([]);

	const [divisionsOptions, setDivisionsOptions] = useState<DropdownOption[]>(
		[]
	);
	const [countriesOptions, setCountriesOptions] = useState<DropdownOption[]>(
		[]
	);
	const [quartersOptions, setQuartersOptions] = useState<string[]>([]);
	const [yearsOptions, setYearsOptions] = useState<number[]>([]);
	const [statusesOptions, setStatusesOptions] = useState<DropdownOption[]>(
		[]
	);

	const [selectedPeriodStatuses, setSelectedPeriodStatuses] = useState<
		PeriodInputStatusesListingResponseModel[]
	>([]);
	const [isSelectAllChecked, setIsSelectAllChecked] = useState<
		boolean | null
	>(null);

	const user: UserResponseModel = useSelector(
		(state: IState) => state.UserReducer.userDetails
	);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchPeriodStatuses = useCallback(() => {
		setLoading(true);

		if (periodQuarterFilter && periodYearFilter) {
			let order = '';
			if (sortOrder) {
				order =
					sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
			}

			const periodStatusesSearchModel: PeriodInputStatusesSearchRequestModel =
				{
					years: periodYearFilter,
					quarters: periodQuarterFilter,
					countriesIds: countryFilter as number[],
					divisionsIds: divisionFilter as number[],
					statusesIds: statusFilter as number[],
					page: currentPage,
					sortBy: sortField,
					order: order
				};

			httpService
				.postGeneric<PeriodInputStatusesSearchResponseModel>(
					`periodInputStatuses/search`,
					JSON.stringify(periodStatusesSearchModel),
					undefined,
					false
				)
				.then((periodStatuses) => {
					setPeriodStatusesSearch(periodStatuses);
					setPeriodStatusesListing(periodStatuses.models);
					setLoading(false);
				})
				.catch((err) => notifierService.error())
				.finally(() => setLoading(false));
		}
	}, [
		httpService,
		notifierService,
		currentPage,
		divisionFilter,
		countryFilter,
		periodQuarterFilter,
		periodYearFilter,
		statusFilter,
		sortField,
		sortOrder
	]);

	const handleEditInputStatuses = async (
		statusDescription: string
	): Promise<void> => {
		setLoading(true);
		const statusId = statusesOptions.find(
			(x) => x.label == statusDescription
		)?.value as number;

		const periodStatusesUpdateModel: PeriodInputStatusUpdateRequestModel = {
			years: periodYearFilter,
			quarters: periodQuarterFilter,
			countriesIds: countryFilter as number[],
			divisionsIds: divisionFilter as number[],
			statusId: statusId,
			idsToUpdate: isSelectAllChecked
				? []
				: selectedPeriodStatuses.map((x) => x.id)
		};

		try {
			if (isSelectAllChecked != null) {
				await httpService.put(
					`periodInputStatuses/ChangeStatus`,
					JSON.stringify(periodStatusesUpdateModel),
					undefined,
					false
				);

				setShouldReload(true);
				setIsSelectAllChecked(null);
				setSelectedPeriodStatuses([]);
			}
		} catch (err) {
			notifierService.error(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchPeriodStatuses();
	}, [fetchPeriodStatuses]);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>(
				`divisions/dropdown/rights?searchAll=true&userId=${user.id}`,
				undefined
			)
			.then((options) => {
				setDivisionsOptions(options);
				setDivisionFilter(
					cookies.periodStatusDivisions !== undefined
						? cookies.periodStatusDivisions
						: []
				);
			})
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>(
				`countries/dropdown/rights?searchAll=true&userId=${user.id}`,
				undefined
			)
			.then((options) => {
				setCountriesOptions(options);
				setCountryFilter(
					cookies.periodStatusCountries !== undefined
						? cookies.periodStatusCountries
						: []
				);
			})
			.catch((err) => notifierService.error());

		httpService
			.get<QuarterlyPeriodsResponseModel>(
				`quarterlyPeriods/dropdown`,
				undefined
			)
			.then((options) => {
				setYearsOptions(options.years);
				setQuartersOptions(options.quarters);
				setPeriodQuarterFilter(
					cookies.periodStatusQuarters !== undefined
						? cookies.periodStatusQuarters
						: [getCurrentQuarter()]
				);
				setPeriodYearFilter(
					cookies.periodStatusYears !== undefined
						? cookies.periodStatusYears
						: [getCurrentYear()]
				);
			})
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>(
				`periodInputStatuses/statusesDropdown`,
				undefined
			)
			.then((options) => {
				setStatusesOptions(options);
				setStatusFilter(
					cookies.periodStatusStatuses !== undefined
						? cookies.periodStatusStatuses
						: []
				);
			})
			.catch((err) => notifierService.error());
	}, [httpService, notifierService, user.id]);

	useEffect(() => {
		if (shouldReload) {
			fetchPeriodStatuses();
			setShouldReload(false);
		}
	}, [shouldReload, fetchPeriodStatuses]);

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	function statusBodyTemplate(
		periodStatus: PeriodInputStatusesListingResponseModel
	) {
		return (
			<span className={getStatusColor(periodStatus.statusDescription)}>
				{periodStatus.statusDescription}
			</span>
		);
	}

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'Open':
				return 'status-open';

			case 'Closed':
				return 'status-closed';

			default:
				return 'status-closed';
		}
	};

	const handleSelectSpecific = (e: DataTableSelectionChangeParams) => {
		if (e.value.length === 0) {
			setIsSelectAllChecked(null);
		} else if (e.value.length === periodStatusesListing.length) {
			setIsSelectAllChecked(true);
		} else {
			setIsSelectAllChecked(false);
		}
		setSelectedPeriodStatuses([...e.value]);
	};

	const clearAllFilters = () => {
		setCountryFilter([]);
		setDivisionFilter([]);
		setStatusFilter([]);
		setPeriodQuarterFilter([]);
		setPeriodYearFilter([]);
	};

	return (
		<>
			<Filters
				divisionsOptions={divisionsOptions}
				countriesOptions={countriesOptions}
				quartersOptions={quartersOptions}
				yearsOptions={yearsOptions}
				statusesOptions={statusesOptions}
				divisionFilter={divisionFilter}
				countryFilter={countryFilter}
				quarterFilter={periodQuarterFilter}
				yearFilter={periodYearFilter}
				statusFilter={statusFilter}
				setDivisionsFilter={setDivisionFilter}
				setCountryFilter={setCountryFilter}
				setQuarterFilter={setPeriodQuarterFilter}
				setYearFilter={setPeriodYearFilter}
				setStatusFilter={setStatusFilter}
				clearAllFilters={clearAllFilters}
			/>

			<div className='period-status__bar'>
				<button
					disabled={isSelectAllChecked === null}
					className='btn secondary'
					onClick={() => handleEditInputStatuses('Open')}
				>
					Reactivate
				</button>
				<button
					disabled={isSelectAllChecked === null}
					className='btn white'
					onClick={() => handleEditInputStatuses('Closed')}
				>
					Close The Period
				</button>
			</div>

			<DataTable
				value={periodStatusesListing}
				responsiveLayout='scroll'
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				selectionMode={'multiple'}
				selection={selectedPeriodStatuses!}
				selectAll={isSelectAllChecked as boolean}
				onSelectionChange={(e) => handleSelectSpecific(e)}
				loading={loading}
			>
				<Column
					selectionMode='multiple'
					style={{ width: '50px', paddingRight: '10px' }}
				/>
				<Column
					sortable
					header='Division code'
					field={nameof<PeriodInputStatusesListingResponseModel>(
						'divisionCode'
					)}
					style={{ width: '120px', padding: '10px 15px 10px 10px' }}
				/>

				<Column
					sortable
					header='Country'
					field={nameof<PeriodInputStatusesListingResponseModel>(
						'countryDescription'
					)}
					style={{ width: '160px', padding: '10px 15px 10px 10px' }}
				/>

				<Column
					sortable
					header='Period quarter'
					field={nameof<PeriodInputStatusesListingResponseModel>(
						'quarterlyPeriodQuarter'
					)}
					style={{ width: '140px', padding: '10px 15px 10px 10px' }}
				/>

				<Column
					sortable
					header='Period year'
					field={nameof<PeriodInputStatusesListingResponseModel>(
						'quarterlyPeriodYear'
					)}
					style={{ width: '120px', padding: '10px 15px 10px 10px' }}
				/>

				<Column
					sortable
					header='Status description'
					body={statusBodyTemplate}
					field={nameof<PeriodInputStatusesListingResponseModel>(
						'statusDescription'
					)}
					style={{ width: '220px', padding: '10px 15px 10px 10px' }}
				/>

				<Column
					style={{ width: '690px' }} //empty column to reduce table size
				/>
			</DataTable>

			<Pagination
				totalRecords={periodStatusesSearch?.total}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default PeriodStatus;
